import { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ScenarioContext, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { saveAssetGroup, loadCampaignAsViewModel, FLOWS } from '@bingads-webui-campaign-react/unified-app-shared';
import { useAppStoreRepository } from '@bingads-webui-campaign-react/app-store-selector';
import { loadAssetGroupWithVideos, loadAssetGroupForWindowsStore } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import cacheManager from '@bingads-webui-campaign-legacy/cache-manager/index';

import {
  getLoggerWithoutPageInfo,
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';

import { createDefaultCampaign, getDefaultAssetGroup } from './model';
import { flowToPlatform } from './model/constants';

export const useAssetsStepRepository = () => {
  const scenarioContext = useContext(ScenarioContext);

  const i18n = useContext(I18nContext);

  const { state, send } = useWizardService();

  const {
    wizardData: { flow },
  } = state.context;

  const permissions = useContext(PermissionsContext);

  const activity = useContext(ActivityContext);

  const versionForLog = getLogVersion(permissions);

  const logger = getLoggerWithoutPageInfo(activity, versionForLog, 'UnifiedAppAdsStep');

  const serverContext = useContext(ServerContext);

  const { scope } = useContext(ScopeContext) || {};

  const { aid, cid } = scope.values;

  const { searchAppById, oDataConfig } = useAppStoreRepository({ platform: flowToPlatform[flow] });

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });

  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });

  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  const uploadVideoIfNeeded = useCallback(({ assetGroup }) => {
    const videoPromises = [];

    if (!assetGroup.videos) {
      return Promise.resolve(assetGroup);
    }

    // check for videos in the assetGroup that dont have an id.
    assetGroup.videos.forEach((video) => {
      if (!video.id) {
        videoPromises.push(uploadVideoMetadata({
          videoMetadata: video,
          newVideoFromMTObject,
          folderRepository,
          imageRepository,
          videoRepository,
        }).then((newVideo) => {
          _.extend(video, newVideo);
        }));
      }
    });

    if (_.isEmpty(videoPromises)) {
      return Promise.resolve(assetGroup);
    }

    return Promise.all(videoPromises).then(() => assetGroup);
  }, [folderRepository, imageRepository, videoRepository]);

  const load = useCallback(() => {
    const { assetGroup: savedAssetGroup, campaign: savedCampaign } = state.context.wizardData;

    const campaignId = savedCampaign ? savedCampaign.Id : null;
    const assetGroupId = savedAssetGroup ? savedAssetGroup.Id : null;

    const saveCampaignsPromise = campaignId
      ? loadCampaignAsViewModel(cid, aid, campaignId)
      : Promise.resolve({});

    const assetGroupPromise = flow === FLOWS.WINDOWS ? loadAssetGroupForWindowsStore : loadAssetGroupWithVideos;

    const savedAssetGroupPromise = assetGroupId
      ? assetGroupPromise(cid, aid, campaignId, assetGroupId, i18n, false, false, videoRepository)
      : Promise.resolve(getDefaultAssetGroup({ i18n }));

    return Promise.props({ campaign: saveCampaignsPromise, assetGroup: savedAssetGroupPromise })
      .then(({ campaign, assetGroup }) => {
        if (_.has(campaign, 'data')) {
          // since the view model conversion adds a data parameter, we dont need it in this wizard.
          // eslint-disable-next-line no-param-reassign
          campaign = campaign.data;
        }
        if (_.has(assetGroup, 'data')) {
          // since the view model conversion adds a data parameter, we dont need it in this wizard.
          // eslint-disable-next-line no-param-reassign
          assetGroup = assetGroup.data;
        }

        return ({
          campaign,
          assetGroup,
        });
      }).then(({ campaign, assetGroup }) => {
        if (campaign && campaign.CampaignSettings && campaign.CampaignSettings.length > 0) {
          const appSettings = campaign.CampaignSettings.find(settings => settings['@odata.type'] === '#Model.AppSetting');
          const { AppId } = appSettings || {};

          return searchAppById(AppId).then((appInfo) => {
            // eslint-disable-next-line no-param-reassign
            campaign.AppSetting = appInfo;
            // eslint-disable-next-line no-param-reassign
            campaign.AppSettingReadOnly = true;

            return {
              campaign,
              assetGroup,
            };
          });
        }
        return {
          campaign,
          assetGroup,
        };
      }).catch((errorData) => {
        activity.trace(`[UnifiedAppWizard] loadData failed. Error: ${errorData}`);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          error: {
            ...errorData,
          },
        });
      });
  }, [activity, i18n, state.context.wizardData, aid, cid, searchAppById, videoRepository]);

  const save = useCallback(({
    data,
    initialData,
  }) => {
    const savedCampaign = state.context.wizardData.campaign;

    const { assetGroup: assetGroupToSave, campaign: campaignToSave } = data;
    const { assetGroup: initialAssetGroup } = initialData;

    const { AppSetting } = campaignToSave;

    const saveCampaignsPromise = savedCampaign
      ? Promise.resolve({
        campaign: {
          data: savedCampaign,
        },
      }) :
      createDefaultCampaign({
        i18n,
        serverContext,
        scenarioContext,
        AppSetting,
        flow,
      });

    return saveCampaignsPromise.then((result) => {
      const { campaign } = result;

      activity.trace('[UnifiedAppWizard] - Default Campaign saved');
      activity.signal(`[UnifiedAppWizard] - Default Campaign saved, campaignId: ${campaign.data.Id}`);

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: result.campaign.data });

      cacheManager.invalidate('AssetGroup');
      cacheManager.invalidate('Campaign');

      return {
        campaign: result.campaign.data,
      };
    }).then((result) => {
      const assetGroupChanges = {
        assetGroup: {
          data: assetGroupToSave,
        },
        originalAssetGroup: {
          data: initialAssetGroup,
        },
        campaign: result.campaign,
      };

      const saveContext = {
        scenario: scenarioContext.name,
        activity,
        activityLogger: scenarioContext.createActivity('saveAssets'),
        i18n,
        serverContext,
        cid,
        aid,
      };

      return Promise.props({
        campaign: Promise.resolve(result.campaign),
        savedAssetGroup: uploadVideoIfNeeded({ assetGroup: assetGroupToSave })
          .then(() => saveAssetGroup(assetGroupChanges, saveContext)),
      });
    }).then((result) => {
      const { savedAssetGroup } = result;

      activity.trace(`[PmaxWizard] saveData succeeded. Assets count: ${result}`);

      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { assetGroup: savedAssetGroup.assetGroup.data });

      // TODO: Uncomment this when copilot recommendations are ready
      // logCopilotRecommendations({
      //   recommendationContext,
      //   assetGroup: savedAssetGroup.assetGroup.data,
      //   campaignId: result.campaign.Id,
      // });

      return {
        campaign: result.campaign,
        assetGroup: savedAssetGroup.assetGroup.data,
      };
    }).catch((errorData) => {
      activity.trace(`[UnifiedAppWizard] saveData failed. Error: ${errorData}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, cid, flow, i18n, scenarioContext, send, serverContext, state.context.wizardData.campaign, uploadVideoIfNeeded]);

  return {
    load,
    save,
  };
};
